import { useCallback } from 'react';
import { capRefusalFor } from '../plans/capRefusal';
import { openDocumentTab, updateDocumentTabLabel, closeDocumentTab } from '../store/tabsStore';
import {
  listTree as listDocTree,
  padKey,
  readDocument,
  createFolder as createDocFolder,
  countOwnDocuments,
  createDocument as createDocRecord,
  saveDocumentBody,
  saveDocumentTitle,
  deleteNodes,
} from '../extensions/bundled/documents/documentsStore';
import { aiCreatedMarks } from '../services/ai/aiAuthorship';
import { bumpDocumentsTree } from '../extensions/bundled/documents/documentsUiStore';
import { getAiDocEditor } from '../services/aiExtensionBridges';
import { workspaceNames, nodeWorkspace, rootCreateScope } from '../services/ai/docWorkspaceScope';
import { joinActiveProjectAfterCreate } from '../services/ai/aiProjectScope';
import { PERSONAL_DOC_SCOPE, docTeamScopes, KEY_STEP, bodyText, toBodyHtml, markDocEdited } from './aiExtensionHelpers';

/** Documents tools for the assistant: list, read, create, write, open, rename, delete. */
export function useAiDocumentActions({
  navigateToPanel,
}) {
  // ── Documents ─────────────────────────────────────────────────────────────
  // The tool schemas for every handler below carry a bare document_id/parent_id
  // with no scope/team_id field, so the tool boundary alone can't say which
  // bucket an id lives in. documentsStore itself has no implicit cross-scope
  // search (unlike boardsStore.readBoard), so resolveDocNodeScope below does
  // it here: personal first, then every team the user belongs to.

  /** Cross-scope lookup for a documents-tree node (folder or document) by id.
   *  Returns the scope it was found in, or null if it isn't in any scope the
   *  user can currently see. */
  const resolveDocNodeScope = useCallback(async (nodeId) => {
    if (!nodeId) return PERSONAL_DOC_SCOPE;
    const scopes = [PERSONAL_DOC_SCOPE, ...docTeamScopes()];
    // eslint-disable-next-line no-restricted-syntax
    for (const scope of scopes) {
      // eslint-disable-next-line no-await-in-loop
      const tree = await listDocTree(scope).catch(() => []);
      if ((tree || []).some((n) => n.id === nodeId)) return scope;
    }
    return null;
  }, []);

  // Next sort key after the last sibling under parentId (mirrors the sidebar).
  const nextDocSortKey = useCallback(async (parentId, scope) => {
    const tree = await listDocTree(scope);
    const siblings = (tree || []).filter((n) => (n.parentId ?? null) === (parentId ?? null));
    const max = siblings.reduce((m, n) => Math.max(m, Number(n.sortKey) || 0), 0);
    return padKey(max + KEY_STEP);
  }, []);

  const getDocuments = useCallback(async () => {
    const scopes = [PERSONAL_DOC_SCOPE, ...docTeamScopes()];
    const [perScope, names] = await Promise.all([
      Promise.all(scopes.map((scope) => listDocTree(scope).catch(() => []).then((tree) => ({ scope, tree })))),
      workspaceNames(),
    ]);
    const documents = [];
    perScope.forEach(({ scope, tree }) => {
      (tree || []).forEach((n) => {
        documents.push({
          id: n.id,
          type: n.type,
          title: n.name,
          parent_id: n.parentId ?? null,
          team_id: scope.type === 'team' ? scope.teamId : null,
          // `personal`, `personal/<id>` (a workspace, card 5.4) or `team:<id>`,
          // plus the workspace's name — the same wire form MCP uses.
          ...nodeWorkspace(n, scope, names),
        });
      });
    });
    return { documents };
  }, []);

  const readDoc = useCallback(async (input) => {
    const docId = String(input.document_id);
    const scope = await resolveDocNodeScope(docId);
    if (!scope) return { error: 'Document not found' };
    const doc = await readDocument(docId, scope);
    if (!doc) return { error: 'Document not found' };
    return { document: { id: doc.id, title: doc.title, body_text: bodyText(doc.bodyHtml).slice(0, 8000) } };
  }, [resolveDocNodeScope]);

  const createDocFolderTool = useCallback(async (input) => {
    const parentId = input.parent_id ? String(input.parent_id) : null;
    // A folder nested under an existing node inherits that node's scope; a
    // root-level folder lands in `input.scope` (Personal or one of the user's
    // workspaces, card 5.4) — personal when absent, as before.
    const root = parentId ? null : await rootCreateScope(input.scope);
    if (root?.error) return { error: root.error };
    const scope = parentId ? (await resolveDocNodeScope(parentId)) || PERSONAL_DOC_SCOPE : root.scope;
    const sortKey = await nextDocSortKey(parentId, scope);
    const folder = await createDocFolder({ parentId, name: input.name || 'New Folder', sortKey }, scope);
    const project = parentId ? null : await joinActiveProjectAfterCreate('folder', folder.id, scope); // card 6.3
    bumpDocumentsTree();
    // Surface the Documents panel so the user sees the new folder appear.
    navigateToPanel('documents.panel');
    return { folder_id: folder.id, ...(project ? { project } : {}) };
  }, [nextDocSortKey, navigateToPanel, resolveDocNodeScope]);

  const createDocumentTool = useCallback(async (input) => {
    const parentId = input.parent_id ? String(input.parent_id) : null;
    const root = parentId ? null : await rootCreateScope(input.scope);
    if (root?.error) return { error: root.error };
    const scope = parentId ? (await resolveDocNodeScope(parentId)) || PERSONAL_DOC_SCOPE : root.scope;
    if (scope?.type !== 'team') {
      const capped = await capRefusalFor('documents', countOwnDocuments);
      if (capped) return { error: capped };
    }
    const sortKey = await nextDocSortKey(parentId, scope);
    const bodyHtml = input.content ? toBodyHtml(input.content, input.format) : '';
    // ai_generated + provider/model + content signature: AI Act Art. 50(2)
    // machine-readable mark (documentsStore.createDocument, aiAuthorship.js).
    const { id } = await createDocRecord(
      { parentId, title: input.title || '', sortKey },
      scope,
      { aiGenerated: true, aiMarks: aiCreatedMarks(bodyHtml) }
    );
    if (input.content) {
      await saveDocumentBody(id, bodyHtml, scope);
    }
    const project = parentId ? null : await joinActiveProjectAfterCreate('document', id, scope); // card 6.3
    bumpDocumentsTree();
    // Always open the new document — creating a doc you can't see is pointless
    // (mirrors db_run_sql always opening the DB Manager grid). Switches to the
    // Documents panel and opens the tab with its content already inside.
    navigateToPanel('documents.panel');
    openDocumentTab({ documentId: id, label: input.title || 'Untitled', scope });
    return { document_id: id, ...(project ? { project } : {}) };
  }, [nextDocSortKey, navigateToPanel, resolveDocNodeScope]);

  const writeDocumentTool = useCallback(async (input) => {
    const docId = String(input.document_id);
    const mode = input.mode === 'append' ? 'append' : 'replace';
    const addition = toBodyHtml(input.content, input.format);

    const scope = await resolveDocNodeScope(docId);
    if (!scope) return { error: 'Document not found' };

    // If the editor is already mounted, drive it live; capture that before we
    // (always) surface the document so the check reflects the pre-open state.
    const liveEditor = getAiDocEditor(docId);
    navigateToPanel('documents.panel');
    openDocumentTab({ documentId: docId, scope });

    if (liveEditor) {
      // Drive the open editor; its autosave persists the change.
      const current = mode === 'append' ? (liveEditor.getBody?.() || '') : '';
      const nextHtml = mode === 'append' ? `${current}${addition}` : addition;
      liveEditor.applyBody(nextHtml);
      // AI Act Art. 50(2): the edit is marked on the meta record (the editor's
      // autosave only writes the body).
      await markDocEdited(docId, nextHtml, scope);
      bumpDocumentsTree();
      return { ok: true };
    }

    // Not open yet — write to the store; the tab we just opened loads it.
    let nextHtml = addition;
    if (mode === 'append') {
      const doc = await readDocument(docId, scope);
      if (!doc) return { error: 'Document not found' };
      nextHtml = `${doc.bodyHtml || ''}${addition}`;
    }
    await saveDocumentBody(docId, nextHtml, scope);
    await markDocEdited(docId, nextHtml, scope);
    bumpDocumentsTree();
    return { ok: true };
  }, [navigateToPanel, resolveDocNodeScope]);

  const openDocumentTool = useCallback(async (input) => {
    const docId = String(input.document_id);
    const scope = await resolveDocNodeScope(docId);
    if (!scope) return { error: 'Document not found' };
    const doc = await readDocument(docId, scope);
    if (!doc) return { error: 'Document not found' };
    navigateToPanel('documents.panel');
    openDocumentTab({ documentId: docId, label: doc.title || 'Untitled', scope });
    return { ok: true, title: doc.title };
  }, [navigateToPanel, resolveDocNodeScope]);

  const renameDocumentTool = useCallback(async (input) => {
    if (!input.title) return { error: 'title is required' };
    const docId = String(input.document_id);
    const scope = await resolveDocNodeScope(docId);
    if (!scope) return { error: 'Document not found' };
    const doc = await readDocument(docId, scope);
    if (!doc) return { error: 'Document not found' };
    await saveDocumentTitle(docId, input.title, scope);
    bumpDocumentsTree();
    // Keep an open tab + its live editor title in sync (the editor watches the
    // tab label), so the rename is reflected without reloading the document.
    updateDocumentTabLabel(docId, input.title);
    return { ok: true, title: input.title };
  }, [resolveDocNodeScope]);

  const deleteDocumentTool = useCallback(async (input) => {
    const docId = String(input.document_id);
    const scope = await resolveDocNodeScope(docId);
    if (!scope) return { error: 'Document not found' };
    const doc = await readDocument(docId, scope);
    if (!doc) return { error: 'Document not found' };
    const title = doc.title || '';
    // deleteNodes tombstones the record (and, for a document, its body row).
    await deleteNodes([{ id: docId, type: 'document' }], scope);
    bumpDocumentsTree();
    closeDocumentTab(docId);
    return { ok: true, title };
  }, [resolveDocNodeScope]);

  return {
    getDocuments,
    readDoc,
    createDocFolderTool,
    createDocumentTool,
    writeDocumentTool,
    openDocumentTool,
    renameDocumentTool,
    deleteDocumentTool,
  };
}
